import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import H6 from '@base/H6/H6';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { getMe } from '@selectors/auth';
import { getPlusSubscriptionInfo } from '@selectors/subscription';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import {
  formatTime,
  compareDates,
  formatMinutesFromSeconds,
  getStartAndEndDateOfWeek,
  getTimeFormatInShortFormWithoutSecs,
  getScheduleDateInfo,
  getWeekDateInfo
} from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { pluralize } from '@utils/textHelper';
import isEmptyUtil from '@utils/isEmpty';
import { Fonts, Spacings, mediaQuery } from '@styles/index';

import { getTotalPracticeDPPCount } from '@selectors/courses';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { useRouter } from 'next/router';
import { getIsOnlyRecordedClassesPresent } from '@api/selectors/course';
import Week from './Week';
import { getCountInfo } from './utils';

const SeeAll = styled(H6)`
  grid-column: 9/25;
  background-color: var(--color-base-1);
  color: var(--color-text-primary);
  height: 70px;
  text-align: center;
  cursor: pointer;
  padding: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    height: auto;
    font-size: ${Fonts.SMALL};
    margin: ${Spacings.SPACING_4B} 0;
  }
`;

const getThreeWeeks = (weeks) => ({
  ...(weeks[1] && { 1: weeks[1] }),
  ...(weeks[2] && { 2: weeks[2] }),
  ...(weeks[3] && { 3: weeks[3] })
});

const getTenLessons = (lessons = []) => lessons.slice(0, 10);

// TODO: Need to merge with CourseSchedule
// Just for learner side
const LeanerCourseSchdule = ({
  course,
  lessonByWeeks,
  isTestSeries,
  allLessons,
  lessonToResume,
  liveLesson,
  onItemClick,
  setAnchorEl,
  selectedLesson,
  renewalData,
  showByWeek,
  hasDppAccess
}) => {
  const router = useRouter();
  const { courseUID } = router.query;
  const { goalUid: goalUID } = course;

  const me = useSelector(getMe);

  const [hideFewWeeks, setHideFewWeeks] = useState(true);

  useFirstRenderSkip(() => {
    setHideFewWeeks(!course?.isEnrolled);
  }, [course?.isEnrolled]);

  const subscription = useSelector(getPlusSubscriptionInfo(goalUID));
  const isSubscribed = hasActiveSubscriptionToGoal(me, goalUID);
  const isOnlyRecordedClassesPresent = getIsOnlyRecordedClassesPresent(course);
  const totalPracticeDPPCount = useSelectorToStore(
    getTotalPracticeDPPCount(courseUID)
  );

  const toggleHideWeeks = () => setHideFewWeeks(false);

  const showEducatorForLesson = course?.authors?.length > 1;

  const getScheduleInfoData = () => {
    return {
      dateInfo: getScheduleDateInfo(
        allLessons[0]?.liveAt,
        allLessons[allLessons.length - 1]?.liveAt
      ),
      lessonCountInfo: getCountInfo(
        course.liveClassCount,
        course.quizCount,
        isTestSeries
      ),
      dppCount: totalPracticeDPPCount
    };
  };

  const getWeekInfoData = (weekNumber) => {
    return {
      dateInfo: getWeekDateInfo(lessonByWeeks[weekNumber].lessons[0]?.liveAt),
      lessonCountInfo: getCountInfo(
        lessonByWeeks[weekNumber]?.lessonCount,
        lessonByWeeks[weekNumber]?.quizCount,
        false
      ),
      dppCount: lessonByWeeks[weekNumber]?.dppCount
    };
  };

  const getCourseInfoForFreeCourse = () => {
    return `${pluralize(
      course.itemCount,
      'lesson'
    )} • ${getTimeFormatInShortFormWithoutSecs(course.totalTime)}`;
  };

  const renderSchedule = () => {
    // TODO: as this is copied from CourseSchedule. Need to checks what this is used for
    const getCardPosition = () => {
      let cardPosition = -1;
      const expiryDate = subscription?.expired_at;
      const expiry = expiryDate && new Date(expiryDate);
      if (compareDates(expiryDate, course.endsAt) !== 1) return -1;
      for (let i = 0; i < allLessons?.length; i += 1) {
        if (expiry < new Date(allLessons[i].liveAt)) {
          cardPosition = allLessons[i].rank - 1;
          break;
        }
      }
      return cardPosition;
    };

    const cardPosition = course?.forSubscription ? getCardPosition() : -1;

    if (showByWeek) {
      // show only 3 weeks if not enrolled
      const weeksShown = hideFewWeeks
        ? getThreeWeeks(lessonByWeeks)
        : lessonByWeeks;

      return Object.keys(weeksShown).map((weekNumber) => {
        const weekTitle = `Week ${weekNumber}`;
        if (!lessonByWeeks[weekNumber]) return null;
        const weekInfo = getStartAndEndDateOfWeek(
          lessonByWeeks[weekNumber].lessons[0]?.liveAt,
          lessonByWeeks[weekNumber].lessonCount,
          lessonByWeeks[weekNumber].quizCount
        );
        return (
          <Week
            key={weekNumber}
            weekNumber={weekNumber}
            lessonList={lessonByWeeks[weekNumber].lessons}
            quizCount={lessonByWeeks[weekNumber].quizCount}
            lessonCount={lessonByWeeks[weekNumber].lessonCount}
            onItemClick={onItemClick}
            weekTitle={weekTitle}
            weekInfo={weekInfo}
            weekPlusInfoData={getWeekInfoData(weekNumber)}
            showEducatorForLesson={showEducatorForLesson}
            isTestSeries={isTestSeries}
            isSubscribed={isSubscribed}
            cardPosition={cardPosition}
            subscription={subscription}
            forPlus={course?.forPlus}
            courseType={course?.courseType}
            setAnchorEl={setAnchorEl}
            selectedLesson={selectedLesson}
            renewalData={renewalData}
            hasDppAccess={hasDppAccess}
            isOnlyRecordedClassesPresent={isOnlyRecordedClassesPresent}
          />
        );
      });
    }

    // show only 10 lessons if not enrolled
    const lessonShown = hideFewWeeks ? getTenLessons(allLessons) : allLessons;

    return (
      <Week
        key={1}
        weekNumber={1}
        lessonList={lessonShown}
        quizCount={course.quizCount}
        lessonCount={course.liveClassCount}
        onItemClick={onItemClick}
        weekTitle={course?.forPlus ? 'Schedule' : 'Lessons'}
        weekInfo={getCourseInfoForFreeCourse()}
        weekPlusInfoData={getScheduleInfoData()}
        showEducatorForLesson={showEducatorForLesson}
        isTestSeries={isTestSeries}
        isSubscribed={isSubscribed}
        forPlus={course?.forPlus}
        courseType={course?.courseType}
        cardPosition={cardPosition}
        subscription={subscription}
        setAnchorEl={setAnchorEl}
        selectedLesson={selectedLesson}
        renewalData={renewalData}
        hasDppAccess={hasDppAccess}
        isOnlyRecordedClassesPresent={isOnlyRecordedClassesPresent}
      />
    );
  };

  const moreLessonsAvailable = showByWeek
    ? !isEmptyUtil(lessonByWeeks[4]) // // if only 3 week lessons are available then do not show 'see all lessons' button
    : !isEmptyUtil(allLessons[10]); // if only 10 lessons are available then do not show 'see all lessons' button

  const seeAllText = isTestSeries ? 'See all tests' : 'See all lessons';

  return (
    <>
      {!!liveLesson.length && (
        <Week
          weekNumber={-1}
          lessonList={liveLesson}
          weekTitle="Live Now"
          isTestSeries={isTestSeries}
          forPlus={course?.forPlus}
          weekInfo={`Started at ${formatTime(
            liveLesson?.[0]?.startedAt || liveLesson?.[0]?.liveAt
          )}`}
          showEducatorForLesson={showEducatorForLesson}
          isSubscribed={isSubscribed}
          onItemClick={onItemClick}
          selectedLesson={selectedLesson}
          courseType={course?.courseType}
          hasDppAccess={hasDppAccess}
          isOnlyRecordedClassesPresent={isOnlyRecordedClassesPresent}
        />
      )}
      {lessonToResume && !liveLesson.length && (
        <Week
          weekNumber={0}
          isTestSeries={isTestSeries}
          lessonList={[lessonToResume]}
          weekTitle="Resume"
          weekInfo={
            lessonToResume.type === 'quiz'
              ? `Quiz ${lessonToResume.contentRank}`
              : `Lesson ${
                  lessonToResume.contentRank
                } from ${formatMinutesFromSeconds(lessonToResume.watchTime)}`
          }
          showEducatorForLesson={showEducatorForLesson}
          isSubscribed={isSubscribed}
          forPlus={course?.forPlus}
          courseType={course?.courseType}
          onItemClick={onItemClick}
          setAnchorEl={setAnchorEl}
          selectedLesson={selectedLesson}
          hasDppAccess={hasDppAccess}
          isOnlyRecordedClassesPresent={isOnlyRecordedClassesPresent}
        />
      )}
      {renderSchedule()}
      {hideFewWeeks && moreLessonsAvailable && (
        <SeeAll onClick={toggleHideWeeks}>+ {seeAllText}</SeeAll>
      )}
    </>
  );
};

export default LeanerCourseSchdule;
